import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AdminShell } from './AdminShell';
import { adminApi, AdminAiRun, Paged } from '@/lib/adminApi';

export function AdminAiRunsPage() {
  const { t } = useTranslation();
  const [agentKind, setAgentKind] = useState('');
  const [model, setModel] = useState('');
  const [sinceDays, setSinceDays] = useState(7);

  const list = useQuery<Paged<AdminAiRun>>({
    queryKey: ['admin', 'ai-runs', agentKind, model, sinceDays],
    queryFn: () =>
      adminApi.listAiRuns({
        agentKind: agentKind || undefined,
        model: model || undefined,
        sinceDays,
        limit: 200,
      }),
  });

  const rows = list.data?.items ?? [];

  return (
    <AdminShell titleKey="admin.ai_runs.title">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          placeholder={t('admin.ai_runs.filter_agent')}
          value={agentKind}
          onChange={(e) => setAgentKind(e.target.value)}
          className="w-44 rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
        <input
          type="search"
          placeholder={t('admin.ai_runs.filter_model')}
          value={model}
          onChange={(e) => setModel(e.target.value)}
          className="w-56 rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
        <label className="text-xs text-ink-600 flex items-center gap-2">
          {t('admin.ai_runs.since_days')}
          <select
            value={sinceDays}
            onChange={(e) => setSinceDays(Number(e.target.value))}
            className="rounded-md border border-ink-300 px-2 py-1 text-sm"
          >
            <option value={1}>1</option>
            <option value={7}>7</option>
            <option value={30}>30</option>
            <option value={90}>90</option>
          </select>
        </label>
        {typeof list.data?.total === 'number' && (
          <span className="text-xs text-ink-500">
            {t('admin.common.total')}: {list.data.total.toLocaleString()}
          </span>
        )}
      </div>

      {list.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}

      {!list.isLoading && rows.length === 0 && (
        <p className="text-sm text-ink-500">{t('admin.ai_runs.empty')}</p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm bg-white rounded-2xl border border-ink-200">
            <thead className="text-left text-ink-500 border-b border-ink-200">
              <tr>
                <th className="py-2.5 px-3 font-medium">{t('admin.ai_runs.col_time')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.ai_runs.col_agent')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.ai_runs.col_model')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.ai_runs.col_workspace')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.ai_runs.col_tokens')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.ai_runs.col_cost')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.ai_runs.col_citations')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-ink-100">
                  <td className="py-2.5 px-3 num text-ink-500 text-xs">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="py-2.5 px-3 font-medium text-ink-900">{r.agent_kind}</td>
                  <td className="py-2.5 px-3 text-ink-700 text-xs">{r.model}</td>
                  <td className="py-2.5 px-3 num text-ink-500 text-xs">
                    {r.workspace_id.slice(0, 8)}
                  </td>
                  <td className="py-2.5 px-3 num text-ink-700 text-xs">
                    {(r.prompt_tokens ?? 0).toLocaleString()} /{' '}
                    {(r.completion_tokens ?? 0).toLocaleString()}
                  </td>
                  <td className="py-2.5 px-3 num text-ink-700">
                    {r.cost_cents != null ? `¥${Math.round(r.cost_cents / 100)}` : '—'}
                  </td>
                  <td className="py-2.5 px-3 text-ink-500 text-xs">
                    {r.cited_record_kinds && r.cited_record_kinds.length > 0
                      ? r.cited_record_kinds.join(', ')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}
